import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  Check,
  Download,
  FileText,
  Loader2,
  MessageSquarePlus,
  Printer,
  RefreshCw,
  Trash2,
  UserCheck,
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  BusinessBand,
  BusinessCaseDetail,
  BusinessCaseRow,
  BusinessCaseStatus,
  BusinessFitDecision,
  businessCasesService,
  coursesService,
} from '../services/api';
import { BusinessFormSettings } from './business/BusinessFormSettings';
import { Course } from '../types';
import { useAuth } from '../contexts/AuthContext';

const fa = (n: number) => n.toLocaleString('fa-IR');
const faDec = (n: number) =>
  n.toLocaleString('fa-IR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** جهت اینجا برعکس پرسشنامهٔ مسیر رشد است: امتیاز پایین یعنی وضعیت بد */
const BAND: Record<BusinessBand, { color: string; label: string }> = {
  CRITICAL: { color: '#d03b3b', label: 'بحرانی' },
  NEEDS_WORK: { color: '#fab219', label: 'نیازمند بهبود' },
  RELIABLE: { color: '#3B82C4', label: 'قابل اتکا' },
  STRENGTH: { color: '#0ca30c', label: 'نقطه قوت' },
};

const STATUS_LABEL: Record<BusinessCaseStatus, string> = {
  DRAFT: 'پیش‌نویس',
  SUBMITTED: 'ثبت شده',
  IN_REVIEW: 'در بررسی',
  TRIAGED: 'تعیین اولویت شده',
  CLOSED: 'بسته',
};

const FIT_LABEL: Record<BusinessFitDecision, string> = {
  FIT: 'مناسب',
  NEEDS_REVIEW: 'نیازمند بررسی بیشتر',
  NOT_FIT: 'فعلاً نامناسب / ارجاع',
};

const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500';

const PROFILE_LABELS: Array<[string, string]> = [
  ['businessName', 'نام کسب‌وکار'],
  ['respondentName', 'تکمیل‌کننده'],
  ['industry', 'صنعت'],
  ['city', 'شهر / بازار'],
  ['businessAge', 'سن کسب‌وکار'],
  ['teamSize', 'اندازهٔ تیم'],
  ['mainProducts', 'محصولات / خدمات'],
  ['targetCustomer', 'مشتری هدف'],
  ['salesChannels', 'کانال‌های فروش'],
  ['mainChallenge', 'مهم‌ترین چالش'],
  ['twelveMonthGoal', 'هدف ۱۲ ماهه'],
  ['positiveTrend', 'روند مثبت'],
  ['worryingTrend', 'روند نگران‌کننده'],
  ['unmeasuredNumber', 'عدد اندازه‌گیری‌نشده'],
];

/** یک ردیف محور روی محور مشترک ۱ تا ۵ */
const AxisRow: React.FC<{ axis: BusinessCaseDetail['axisScores'][number] }> = ({ axis }) => {
  const pct = ((axis.average - 1) / 4) * 100;
  return (
    <div className="flex items-center gap-3">
      <div className="w-32 shrink-0">
        <p className="truncate text-xs font-bold text-gray-800">{axis.title}</p>
        <p className="truncate text-[10px] text-gray-400">{axis.subtitle}</p>
      </div>
      <div className="relative h-5 flex-1">
        <span className="absolute inset-y-0 right-0 my-auto h-1.5 w-full rounded-full bg-gray-100" />
        <span
          className="absolute inset-y-0 my-auto h-1.5 rounded-full"
          style={{ right: 0, width: `${pct}%`, backgroundColor: BAND[axis.band].color, opacity: 0.35 }}
        />
        <span
          className="absolute inset-y-0 my-auto h-3 w-3 rounded-full border-2 border-white shadow"
          style={{
            right: `${pct}%`,
            transform: 'translateX(50%)',
            backgroundColor: BAND[axis.band].color,
          }}
        />
      </div>
      <span
        className="w-20 shrink-0 text-left text-xs font-black tabular-nums"
        style={{ color: BAND[axis.band].color }}
      >
        {faDec(axis.average)}
        <span className="text-[10px] font-bold text-gray-400"> ({fa(axis.total)}/۲۵)</span>
      </span>
    </div>
  );
};

type TabKey = 'cases' | 'invites';

const BusinessCases: React.FC = () => {
  const { user } = useAuth();
  /**
   * منتور همین صفحه را می‌بیند ولی سرور دو کار را برایش نمی‌پذیرد: ارجاع پرونده
   * به منتور دیگر، و تنظیمات فرم و لینک‌های دعوت. اگر اینجا نشانشان بدهیم،
   * فقط ۴۰۳ می‌گیرد. پس همان‌ها را از دیدش برمی‌داریم.
   */
  const canManageForm = user?.role === 'ADMIN' || user?.role === 'STORE';

  const [tab, setTab] = useState<TabKey>('cases');
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [rows, setRows] = useState<BusinessCaseRow[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<BusinessCaseStatus | ''>('');
  const [mentors, setMentors] = useState<
    Array<{ id: string; firstName?: string | null; lastName?: string | null }>
  >([]);

  const [exporting, setExporting] = useState(false);

  const [openId, setOpenId] = useState('');
  const [detail, setDetail] = useState<BusinessCaseDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [noteBody, setNoteBody] = useState('');
  const [review, setReview] = useState<Record<string, string>>({});
  const [priorities, setPriorities] = useState<
    Array<{ action: string; owner: string; dueDate: string; successMetric: string }>
  >([]);

  const flash = (m: string) => {
    setNotice(m);
    setTimeout(() => setNotice(''), 2800);
  };
  const fail = (err: any, fallback: string) => {
    setError(err?.response?.data?.message || fallback);
    setTimeout(() => setError(''), 6000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, mentorList, courseList] = await Promise.all([
        businessCasesService.list({
          status: statusFilter || undefined,
          search: search.trim() || undefined,
          take: 100,
        }),
        businessCasesService.listMentors().catch(() => []),
        coursesService.getAll().catch(() => [] as Course[]),
      ]);
      setRows(list.cases);
      setTotal(list.total);
      setMentors(mentorList);
      setCourses(courseList);
    } catch (err: any) {
      fail(err, 'دریافت پرونده‌ها انجام نشد.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  /** خروجی اکسل با همان فیلترهای فعلی صفحه */
  const exportExcel = async () => {
    setExporting(true);
    try {
      const blob = await businessCasesService.exportExcel({
        status: statusFilter || undefined,
        search: search.trim() || undefined,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `business-cases-6c-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      flash('فایل اکسل ساخته شد.');
    } catch (err: any) {
      fail(err, 'ساخت فایل اکسل انجام نشد.');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const openCase = async (caseId: string) => {
    setOpenId(caseId);
    setDetailLoading(true);
    try {
      const data = await businessCasesService.get(caseId);
      setDetail(data);
      setReview({
        coreProblem: data.review?.coreProblem || '',
        rootCause: data.review?.rootCause || '',
        costOfInaction: data.review?.costOfInaction || '',
        ninetyDayOutcome: data.review?.ninetyDayOutcome || '',
        fitDecision: data.review?.fitDecision || '',
        suggestedPath: data.review?.suggestedPath || '',
        timeCommitment: data.review?.timeCommitment || '',
        successCriteria: data.review?.successCriteria || '',
        meetingDate: data.review?.meetingDate || '',
      });
      setPriorities(
        data.priorities.length
          ? data.priorities.map((p) => ({
              action: p.action,
              owner: p.owner || '',
              dueDate: p.dueDate || '',
              successMetric: p.successMetric || '',
            }))
          : [
              { action: '', owner: '', dueDate: '', successMetric: '' },
              { action: '', owner: '', dueDate: '', successMetric: '' },
              { action: '', owner: '', dueDate: '', successMetric: '' },
            ],
      );
    } catch (err: any) {
      fail(err, 'دریافت پرونده انجام نشد.');
      setOpenId('');
    } finally {
      setDetailLoading(false);
    }
  };

  const run = async (fn: () => Promise<void>, fallback: string) => {
    setBusy(true);
    try {
      await fn();
    } catch (err: any) {
      fail(err, fallback);
    } finally {
      setBusy(false);
    }
  };

  const answersByAxis = useMemo(() => {
    if (!detail) return new Map<string, BusinessCaseDetail['answers']>();
    const map = new Map<string, BusinessCaseDetail['answers']>();
    for (const answer of detail.answers) {
      const list = map.get(answer.axisId) ?? [];
      list.push(answer);
      map.set(answer.axisId, list);
    }
    return map;
  }, [detail]);

  if (loading) return <LoadingSpinner />;

  return (
    <div dir="rtl">
      <PageHeader
        title="پرونده‌های کسب‌وکار"
        description="ارزیابی اولیه 6C — نتیجه، پرچم‌های قرمز و جمع‌بندی جلسهٔ تشخیص"
        action={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={exportExcel}
              disabled={exporting || !rows.length}
              className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
              title="خروجی کامل برای تیم منتورها — با همین فیلترهای صفحه"
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              {exporting ? 'در حال ساخت…' : 'خروجی اکسل منتورها'}
            </button>
            <button
              type="button"
              onClick={load}
              className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-bold text-gray-700"
            >
              <RefreshCw className="h-4 w-4" />
              تازه‌سازی
            </button>
          </div>
        }
      />

      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
          {error}
        </div>
      ) : null}
      {notice ? (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm font-bold text-green-700">
          {notice}
        </div>
      ) : null}

      <div className="mb-4 flex gap-1 border-b border-gray-200">
        {(
          [
            ['cases', 'پرونده‌ها'],
            ...(canManageForm
              ? ([['invites', 'لینک‌های دعوت و تنظیمات فرم']] as const)
              : []),
          ] as ReadonlyArray<readonly [TabKey, string]>
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm font-bold transition ${
              tab === key
                ? 'border-b-2 border-blue-600 text-blue-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'invites' && canManageForm ? <BusinessFormSettings courses={courses} /> : null}

      <div className={tab === 'cases' ? '' : 'hidden'}>
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white p-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="نام کسب‌وکار، نام یا موبایل…"
          className={`${inputClass} w-auto min-w-[240px]`}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as BusinessCaseStatus | '')}
          className={`${inputClass} w-auto`}
        >
          <option value="">همهٔ وضعیت‌ها</option>
          {(['SUBMITTED', 'IN_REVIEW', 'TRIAGED', 'CLOSED'] as const).map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        <span className="text-sm font-bold text-gray-500">{fa(total)} پرونده</span>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="w-full min-w-[900px] text-right text-sm">
          <thead className="bg-gray-50 text-[11px] font-bold text-gray-500">
            <tr>
              <th className="px-4 py-3">کسب‌وکار</th>
              <th className="px-4 py-3">صاحب</th>
              <th className="px-4 py-3">امتیاز کل</th>
              <th className="px-4 py-3">ضعیف‌ترین محور</th>
              <th className="px-4 py-3">پرچم قرمز</th>
              <th className="px-4 py-3">منتور</th>
              <th className="px-4 py-3">وضعیت</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row) => (
              <tr
                key={row.id}
                onClick={() => openCase(row.id)}
                className={`cursor-pointer hover:bg-blue-50/40 ${
                  row.stabilizeFirst ? 'bg-red-50/40' : ''
                }`}
              >
                <td className="px-4 py-3">
                  <p className="font-bold text-gray-900">{row.businessName || '—'}</p>
                  <p className="text-[11px] text-gray-400">{row.industry || ''}</p>
                </td>
                <td className="px-4 py-3 text-xs text-gray-600">
                  {[row.user.firstName, row.user.lastName].filter(Boolean).join(' ') || '—'}
                  <span className="block text-[11px] text-gray-400">{row.user.phone}</span>
                </td>
                <td className="px-4 py-3">
                  {row.overallScore !== null && row.band ? (
                    <span
                      className="rounded-full px-2 py-0.5 text-xs font-black tabular-nums"
                      style={{
                        backgroundColor: `${BAND[row.band].color}14`,
                        color: BAND[row.band].color,
                      }}
                    >
                      {faDec(row.overallScore)} · {BAND[row.band].label}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-gray-600">{row.weakestAxis?.title || '—'}</td>
                <td className="px-4 py-3">
                  {row.redFlagCount ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-black text-red-700">
                      <AlertTriangle className="h-3 w-3" />
                      {fa(row.redFlagCount)}
                    </span>
                  ) : (
                    <span className="text-[11px] text-gray-300">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-gray-600">
                  {row.mentor
                    ? [row.mentor.firstName, row.mentor.lastName].filter(Boolean).join(' ')
                    : <span className="text-amber-600">ارجاع نشده</span>}
                </td>
                <td className="px-4 py-3 text-[11px] font-bold text-gray-500">
                  {STATUS_LABEL[row.status]}
                </td>
              </tr>
            ))}
            {!rows.length ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center font-bold text-gray-400">
                  پرونده‌ای یافت نشد.
                  {!canManageForm ? (
                    /* منتور فقط پرونده‌های ارجاع‌شده به خودش را می‌بیند؛ خالی بودن
                       جدول یعنی هنوز ارجاعی نداشته، نه اینکه پرونده‌ای وجود ندارد */
                    <span className="mt-1 block text-xs font-bold text-gray-400">
                      پرونده‌های ارجاع‌شده به شما اینجا می‌آیند.
                    </span>
                  ) : null}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      </div>

      {/* ── پروندهٔ کامل ── */}
      {openId ? (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 print:static print:bg-white print:p-0"
          onClick={() => setOpenId('')}
        >
          <div
            className="my-6 w-full max-w-4xl rounded-2xl bg-white p-5 print:my-0 print:max-w-none print:rounded-none"
            onClick={(e) => e.stopPropagation()}
          >
            {detailLoading || !detail ? (
              <div className="flex items-center justify-center gap-2 py-24 text-sm font-bold text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                در حال بارگذاری پرونده…
              </div>
            ) : (
              <>
                <div className="mb-5 flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 pb-4">
                  <div>
                    <p className="flex items-center gap-2 text-lg font-black text-gray-900">
                      <Building2 className="h-5 w-5 text-blue-600" />
                      {detail.businessName || 'بدون نام'}
                    </p>
                    <p className="mt-1 text-xs font-bold text-gray-500">
                      {[detail.user.firstName, detail.user.lastName].filter(Boolean).join(' ')}
                      {detail.user.phone ? ` · ${detail.user.phone}` : ''}
                      {detail.submittedAt
                        ? ` · ${new Date(detail.submittedAt).toLocaleDateString('fa-IR')}`
                        : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 print:hidden">
                    <button
                      type="button"
                      onClick={() => window.print()}
                      className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-bold text-gray-700"
                    >
                      <Printer className="h-3.5 w-3.5" />
                      چاپ
                    </button>
                    <button
                      type="button"
                      onClick={() => setOpenId('')}
                      className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-bold text-gray-600"
                    >
                      بستن
                    </button>
                  </div>
                </div>

                {/* قاعدهٔ توقف — بالاترین چیزی که منتور باید ببیند */}
                {detail.stabilizeFirst ? (
                  <div className="mb-4 rounded-xl border border-red-300 bg-red-50 p-4">
                    <p className="flex items-center gap-2 text-sm font-black text-red-800">
                      <AlertTriangle className="h-4 w-4" />
                      قاعدهٔ توقف فعال است — {fa(detail.redFlagCount)} پرچم قرمز
                    </p>
                    <p className="mt-1 text-xs font-bold text-red-700">
                      اولویت این پرونده «تثبیت و کاهش ریسک» است، نه رشد؛ حتی اگر امتیاز کل خوب
                      باشد.
                    </p>
                    <ul className="mt-2 space-y-1">
                      {detail.redFlags
                        .filter((f) => f.value)
                        .map((f) => (
                          <li key={f.flagId} className="text-xs font-bold text-red-800">
                            • {f.text}
                            {f.note ? (
                              <span className="font-normal text-red-600"> — {f.note}</span>
                            ) : null}
                            {f.actionHint ? (
                              <span className="mt-0.5 block pr-3 text-[11px] font-normal text-red-500">
                                اقدام: {f.actionHint}
                              </span>
                            ) : null}
                          </li>
                        ))}
                    </ul>
                  </div>
                ) : null}

                <div className="grid gap-4 lg:grid-cols-[1fr_260px]">
                  <div className="rounded-xl border border-gray-200 p-4">
                    <p className="mb-3 text-sm font-bold text-gray-800">وضعیت شش محور</p>
                    <div className="space-y-2">
                      {detail.axisScores.map((axis) => (
                        <AxisRow key={axis.axisId} axis={axis} />
                      ))}
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-200 p-4 text-center">
                    <p className="text-[11px] font-bold text-gray-400">امتیاز کل</p>
                    {detail.overallScore !== null && detail.band ? (
                      <>
                        <p
                          className="mt-1 text-3xl font-black tabular-nums"
                          style={{ color: BAND[detail.band].color }}
                        >
                          {faDec(detail.overallScore)}
                          <span className="text-sm text-gray-300"> / ۵</span>
                        </p>
                        <p className="mt-1 text-xs font-black" style={{ color: BAND[detail.band].color }}>
                          {detail.bandLabel}
                        </p>
                        <p className="mt-2 text-[11px] font-bold leading-6 text-gray-500">
                          {detail.direction}
                        </p>
                      </>
                    ) : null}
                    <div className="mt-3 border-t border-gray-100 pt-3 text-right print:hidden">
                      <label className="mb-1 block text-[11px] font-bold text-gray-500">منتور</label>
                      {canManageForm ? (
                      <select
                        value={detail.mentor?.id || ''}
                        onChange={(e) =>
                          run(async () => {
                            await businessCasesService.assign(detail.id, e.target.value || null);
                            await openCase(detail.id);
                            await load();
                            flash('ارجاع ثبت شد.');
                          }, 'ارجاع انجام نشد.')
                        }
                        className={inputClass}
                      >
                        <option value="">— ارجاع نشده —</option>
                        {mentors.map((m) => (
                          <option key={m.id} value={m.id}>
                            {[m.firstName, m.lastName].filter(Boolean).join(' ')}
                          </option>
                        ))}
                      </select>
                      ) : (
                        <p className="text-xs font-bold text-gray-700">
                          {detail.mentor
                            ? [detail.mentor.firstName, detail.mentor.lastName]
                                .filter(Boolean)
                                .join(' ')
                            : '— ارجاع نشده —'}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* اطلاعات پایه */}
                <details className="mt-4 rounded-xl border border-gray-200 p-4" open>
                  <summary className="cursor-pointer text-sm font-bold text-gray-800">
                    اطلاعات پایه و تصویر عددی
                  </summary>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    {PROFILE_LABELS.map(([key, label]) =>
                      detail.profile[key] ? (
                        <div key={key} className="rounded-lg border border-gray-100 px-3 py-2">
                          <p className="text-[10.5px] font-bold text-gray-400">{label}</p>
                          <p className="mt-0.5 text-xs text-gray-800">{detail.profile[key]}</p>
                        </div>
                      ) : null,
                    )}
                  </div>

                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full min-w-[560px] text-right text-xs">
                      <thead className="text-[10.5px] font-bold text-gray-400">
                        <tr>
                          <th className="px-2 pb-1.5">شاخص</th>
                          <th className="px-2 pb-1.5">فعلی</th>
                          <th className="px-2 pb-1.5">هدف ۱۲ماهه</th>
                          <th className="px-2 pb-1.5">منبع</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {detail.metrics.map((m) => (
                          <tr key={m.metricId} className={m.notMeasured ? 'bg-amber-50/50' : ''}>
                            <td className="px-2 py-1.5 font-bold text-gray-700">{m.title}</td>
                            {m.notMeasured ? (
                              <td colSpan={3} className="px-2 py-1.5 font-bold text-amber-700">
                                اندازه‌گیری نمی‌شود
                              </td>
                            ) : (
                              <>
                                <td className="px-2 py-1.5 text-gray-800">{m.current || '—'}</td>
                                <td className="px-2 py-1.5 text-gray-600">{m.target || '—'}</td>
                                <td className="px-2 py-1.5 text-gray-400">{m.source || '—'}</td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>

                {/* پاسخ‌ها با شاهد */}
                <details className="mt-3 rounded-xl border border-gray-200 p-4">
                  <summary className="cursor-pointer text-sm font-bold text-gray-800">
                    پاسخ تک‌تک گزاره‌ها و شاهدها
                  </summary>
                  <div className="mt-3 space-y-3">
                    {detail.axisScores.map((axis) => (
                      <div key={axis.axisId}>
                        <p className="mb-1 text-xs font-black text-gray-700">
                          {axis.title} — {axis.subtitle}
                        </p>
                        <div className="space-y-1">
                          {(answersByAxis.get(axis.axisId) ?? []).map((a) => (
                            <div
                              key={a.statementId}
                              className="flex items-start gap-2 rounded-lg border border-gray-100 px-2.5 py-1.5"
                            >
                              <span
                                className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-[10px] font-black text-white"
                                style={{
                                  backgroundColor:
                                    a.score <= 2 ? '#d03b3b' : a.score === 3 ? '#fab219' : '#0ca30c',
                                }}
                              >
                                {fa(a.score)}
                              </span>
                              <div className="min-w-0 flex-1">
                                <p className="text-[11.5px] text-gray-700">{a.text}</p>
                                {a.evidence ? (
                                  <p className="mt-0.5 text-[10.5px] font-bold text-blue-700">
                                    شاهد: {a.evidence}
                                  </p>
                                ) : null}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </details>

                {/* بخش F و G */}
                <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50/40 p-4">
                  <p className="mb-3 flex items-center gap-2 text-sm font-bold text-gray-900">
                    <FileText className="h-4 w-4 text-blue-600" />
                    جمع‌بندی جلسهٔ تشخیص
                  </p>

                  <div className="grid gap-3 sm:grid-cols-2">
                    {(
                      [
                        ['coreProblem', 'مسئله محوری کسب‌وکار'],
                        ['rootCause', 'علت ریشه‌ای احتمالی'],
                        ['costOfInaction', 'هزینه ادامه وضعیت فعلی'],
                        ['ninetyDayOutcome', 'نتیجه مطلوب در ۹۰ روز'],
                      ] as const
                    ).map(([key, label]) => (
                      <label key={key} className="block">
                        <span className="mb-1 block text-[11px] font-bold text-gray-600">
                          {label}
                        </span>
                        <textarea
                          rows={2}
                          value={review[key] || ''}
                          onChange={(e) => setReview((r) => ({ ...r, [key]: e.target.value }))}
                          className={inputClass}
                        />
                      </label>
                    ))}
                  </div>

                  <div className="mt-3 grid gap-3 sm:grid-cols-3">
                    <label className="block">
                      <span className="mb-1 block text-[11px] font-bold text-gray-600">
                        تناسب با دوره
                      </span>
                      <select
                        value={review.fitDecision || ''}
                        onChange={(e) => setReview((r) => ({ ...r, fitDecision: e.target.value }))}
                        className={inputClass}
                      >
                        <option value="">— انتخاب کنید —</option>
                        {(Object.keys(FIT_LABEL) as BusinessFitDecision[]).map((key) => (
                          <option key={key} value={key}>
                            {FIT_LABEL[key]}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-[11px] font-bold text-gray-600">
                        تعهد زمانی شرکت‌کننده
                      </span>
                      <input
                        value={review.timeCommitment || ''}
                        onChange={(e) =>
                          setReview((r) => ({ ...r, timeCommitment: e.target.value }))
                        }
                        className={inputClass}
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-[11px] font-bold text-gray-600">
                        تاریخ جلسه
                      </span>
                      <input
                        value={review.meetingDate || ''}
                        placeholder="۱۴۰۵/۰۵/۲۴"
                        onChange={(e) => setReview((r) => ({ ...r, meetingDate: e.target.value }))}
                        className={inputClass}
                      />
                    </label>
                  </div>

                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    {(
                      [
                        ['suggestedPath', 'مسیر پیشنهادی'],
                        ['successCriteria', 'معیار موفقیت دوره'],
                      ] as const
                    ).map(([key, label]) => (
                      <label key={key} className="block">
                        <span className="mb-1 block text-[11px] font-bold text-gray-600">
                          {label}
                        </span>
                        <textarea
                          rows={2}
                          value={review[key] || ''}
                          onChange={(e) => setReview((r) => ({ ...r, [key]: e.target.value }))}
                          className={inputClass}
                        />
                      </label>
                    ))}
                  </div>

                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      run(async () => {
                        await businessCasesService.saveReview(detail.id, review);
                        flash('جمع‌بندی ذخیره شد.');
                        await load();
                      }, 'ذخیرهٔ جمع‌بندی انجام نشد.')
                    }
                    className="mt-3 flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50 print:hidden"
                  >
                    <Check className="h-4 w-4" />
                    ذخیرهٔ جمع‌بندی
                  </button>
                </div>

                {/* سه اولویت */}
                <div className="mt-4 rounded-xl border border-gray-200 p-4">
                  <p className="mb-3 text-sm font-bold text-gray-900">سه اولویت ۹۰ روزه</p>
                  <div className="space-y-2">
                    {priorities.map((p, index) => (
                      <div key={index} className="grid gap-2 sm:grid-cols-[24px_2fr_1fr_1fr_1.5fr]">
                        <span className="flex h-9 w-6 items-center justify-center text-xs font-black text-gray-300">
                          {fa(index + 1)}
                        </span>
                        {(
                          [
                            ['action', 'اقدام مشخص'],
                            ['owner', 'مسئول'],
                            ['dueDate', 'مهلت'],
                            ['successMetric', 'معیار موفقیت'],
                          ] as const
                        ).map(([key, placeholder]) => (
                          <input
                            key={key}
                            value={p[key]}
                            placeholder={placeholder}
                            onChange={(e) =>
                              setPriorities((list) =>
                                list.map((row, i) =>
                                  i === index ? { ...row, [key]: e.target.value } : row,
                                ),
                              )
                            }
                            className={inputClass}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      run(async () => {
                        const filled = priorities.filter((p) => p.action.trim());
                        if (!filled.length) {
                          setError('حداقل یک اولویت با «اقدام مشخص» وارد کنید.');
                          return;
                        }
                        await businessCasesService.setPriorities(
                          detail.id,
                          filled.map((p) => ({
                            action: p.action,
                            owner: p.owner || undefined,
                            dueDate: p.dueDate || undefined,
                            successMetric: p.successMetric || undefined,
                          })),
                        );
                        flash('اولویت‌ها ثبت شد.');
                        await openCase(detail.id);
                        await load();
                      }, 'ثبت اولویت‌ها انجام نشد.')
                    }
                    className="mt-3 flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50 print:hidden"
                  >
                    <UserCheck className="h-4 w-4" />
                    ثبت اولویت‌ها
                  </button>
                </div>

                {/* یادداشت‌ها */}
                <div className="mt-4 rounded-xl border border-gray-200 p-4 print:hidden">
                  <p className="mb-3 text-sm font-bold text-gray-900">یادداشت‌های جلسات</p>
                  <div className="flex gap-2">
                    <input
                      value={noteBody}
                      onChange={(e) => setNoteBody(e.target.value)}
                      placeholder="یادداشت جدید…"
                      className={inputClass}
                    />
                    <button
                      type="button"
                      disabled={busy || !noteBody.trim()}
                      onClick={() =>
                        run(async () => {
                          await businessCasesService.addNote(detail.id, noteBody.trim());
                          setNoteBody('');
                          await openCase(detail.id);
                        }, 'ثبت یادداشت انجام نشد.')
                      }
                      className="flex shrink-0 items-center gap-1.5 rounded-lg bg-gray-800 px-4 py-2 text-sm font-bold text-white disabled:opacity-40"
                    >
                      <MessageSquarePlus className="h-4 w-4" />
                      ثبت
                    </button>
                  </div>

                  <div className="mt-3 space-y-2">
                    {detail.notes.map((note) => (
                      <div
                        key={note.id}
                        className="flex items-start gap-2 rounded-lg border border-gray-100 px-3 py-2"
                      >
                        <ArrowRight className="mt-1 h-3 w-3 shrink-0 text-gray-300" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-gray-800">{note.body}</p>
                          <p className="mt-0.5 text-[10.5px] text-gray-400">
                            {[note.author.firstName, note.author.lastName]
                              .filter(Boolean)
                              .join(' ')}{' '}
                            · {new Date(note.createdAt).toLocaleDateString('fa-IR')}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            run(async () => {
                              if (!window.confirm('این یادداشت حذف شود؟')) return;
                              await businessCasesService.deleteNote(note.id);
                              await openCase(detail.id);
                            }, 'حذف یادداشت انجام نشد.')
                          }
                          className="shrink-0 rounded p-1 text-red-500 hover:bg-red-50"
                          aria-label="حذف"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                    {!detail.notes.length ? (
                      <p className="py-4 text-center text-xs font-bold text-gray-400">
                        هنوز یادداشتی ثبت نشده است.
                      </p>
                    ) : null}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default BusinessCases;
